/**
 * The client brief pop-up — prototype `openBrief` (docs/prototype.html 3647): header (logo, priority, SPOC,
 * segment, since; the yearly figure for managers), "Where we stand.", usage by product, engagement this month,
 * what is open, ideas to lift adoption, and "Copy as a WhatsApp update". Guide §5.3 adds the health score with
 * its reasons and the last touchpoints; the contract serves this quarter's targets too. One payload:
 * GET /clients/{id}/brief. `revenue` is only in the payload for managers, so it is only drawn when present.
 *
 * Opened anywhere with `?brief=<client id>` (`BriefHost`), so Team → Client briefs and At risk share it.
 */
import { useSearchParams, useNavigate } from 'react-router';
import { TARGET_STATE_LABEL, type ClientBrief, type Reason, type TargetState } from '@amplispot/contracts';
import { useBrief, STATE_CLS } from '../../lib/engagement';
import { dayLabel, firstName, monthYear, rupees } from '../../lib/format';
import { nf, toneOf } from '../../lib/usage';
import { useSession } from '../../lib/session';
import { UNSHOWN_REASONS_LINE, scoreHasUnshownReasons } from '../../lib/health';
import { ApiFailure } from '../../api';
import { ErrorState, HealthChip, Loading, Modal, PriorityChip, bandInfo, useToast } from '../../ui/kit';
import { daysInfo, Spark } from '../usage/parts';
import { copyText } from '../timesheets/TsModals';
import { ClientLogo } from './parts';
import { Lbl } from '../../shell/Help';

const TARGET_CLS: Record<TargetState, string> = { achieved: 'hot', close: 'warm', behind: 'cold' };
/** Prototype openBrief: when the activity was last done — "today", "Nd ago" or "never" (a booked one keeps its booking line). */
function agoLabel(lastDone: string | null, asOf: string): string {
  if (!lastDone) return 'never';
  const d = Math.round((Date.parse(asOf) - Date.parse(lastDone)) / 86_400_000);
  return d <= 0 ? 'today' : `${d}d ago`;
}
const REASON_DOT: Record<Reason['band'], string> = { bad: 'cold', warn: 'warm', ok: 'hot' };

/** Mounts the brief for `?brief=<id>` on the current page; closing drops the parameter. */
export function BriefHost() {
  const [sp, setSp] = useSearchParams();
  const id = sp.get('brief');
  if (!id) return null;
  return (
    <ClientBriefModal
      clientId={id}
      onClose={() =>
        setSp((p) => {
          const n = new URLSearchParams(p);
          n.delete('brief');
          return n;
        })
      }
    />
  );
}

export function ClientBriefModal({ clientId, onClose }: { clientId: string; onClose: () => void }) {
  const q = useBrief(clientId);
  const toast = useToast();
  const nav = useNavigate();
  const { me, can } = useSession();
  const b = q.data as ClientBrief | undefined;
  const usage = me.menu.find((m) => m.key === 'usage');

  if (!b)
    return (
      <Modal title="Client brief" onClose={onClose} wide footer={<button className="btn" onClick={onClose}>Close</button>}>
        {q.error instanceof ApiFailure && q.error.status === 404 ? (
          // Not found and not visible look the same (404, never 403): retrying cannot help, so no Try again.
          <div className="empty">
            <b>No brief to show</b>This client does not exist, or is not one you can see.
          </div>
        ) : q.error ? (
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        ) : (
          <Loading label="Loading the brief…" />
        )}
      </Modal>
    );

  const c = b.client;
  const o = b.open_work_summary;
  const h = b.health;
  // Without toneReason.read the tone lines are left out but their points stay in the score (review t-2d1f14a2 #7).
  const unshown = !!h && !can('toneReason.read') && scoreHasUnshownReasons(h);
  return (
    <Modal
      wide
      onClose={onClose}
      avatar={<ClientLogo id={c.id} name={c.name} size={44} />}
      title={c.name}
      sub={
        <>
          <PriorityChip p={c.priority} /> SPOC {c.spoc.name} · {c.segment} · client since {monthYear(b.since)}
          {b.revenue ? ` · ${rupees(b.revenue.annual_from_mrr_lakh)} a year` : ''}
        </>
      }
      footer={
        <>
          <button
            className="btn"
            onClick={() => {
              onClose();
              nav(`/clients/book?client=${encodeURIComponent(c.id)}`);
            }}
          >
            Open client page
          </button>
          {usage ? (
            <button
              className="btn ghost"
              onClick={() => {
                onClose();
                nav(`/usage/client/${encodeURIComponent(c.id)}`);
              }}
            >
              See full usage
            </button>
          ) : null}
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Close
          </button>
          <button className="btn pri" onClick={() => copyText(b.share_text, () => toast('Copied. Paste it into WhatsApp.'))}>
            Copy as a WhatsApp update
          </button>
        </>
      }
    >
      <div className="note" style={{ marginBottom: 14 }}>
        <b>Where we stand.</b> {b.where_we_stand}
      </div>

      <div className="lbl" style={{ marginBottom: 6 }}>
        Health score
      </div>
      <div className="panel" style={{ marginBottom: 14 }}>
        <div className="hd">
          <h3>{h ? `Why the health reads ${bandInfo(h.band).label.toLowerCase()}` : 'Health score'}</h3>
          <div className="r">
            <HealthChip band={h?.band ?? null} score={h?.value ?? null} />
          </div>
        </div>
        {h ? (
          <>
          {h.explain.length ? (
            h.explain.map((r, i) => (
              <div key={`${r.signal}-${i}`} className="li">
                <span className={`dot ${REASON_DOT[r.band]}`} style={{ marginTop: 6 }} />
                <div className="b">{r.detail}</div>
                {r.penalty ? <span className="sub mono">{r.penalty > 0 ? `+${r.penalty}` : r.penalty}</span> : null}
                {r.source === 'mail' ? <span className="chip info">From emails</span> : null}
              </div>
            ))
          ) : unshown ? null : (
            <div className="empty">
              <b>No warning signs</b>
            </div>
          )}
          {unshown ? (
            <div className="li">
              <span className="dot" style={{ marginTop: 6 }} />
              <div className="b sub">{UNSHOWN_REASONS_LINE}</div>
            </div>
          ) : null}
          </>
        ) : (
          <div className="empty">
            <b>No score yet</b>The health score starts once usage and activity are recorded.
          </div>
        )}
      </div>

      <div className="lbl" style={{ marginBottom: 6 }}>
        Usage by product
      </div>
      <div className="panel" style={{ marginBottom: 14, overflowX: 'auto' }}>
        {b.products.length ? (
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>This period</th>
                <th><Lbl>Vs own usual</Lbl></th>
                <th>Last 14 days</th>
                <th>Other numbers</th>
              </tr>
            </thead>
            <tbody>
              {b.products.map((p) => {
                const ex = p.extras.filter((e) => e.value != null);
                return (
                  <tr key={p.product_key}>
                    <td>
                      <b>{p.product_name}</b>
                      <div className="sub">{p.main_metric}</div>
                    </td>
                    <td>
                      {p.util == null ? (
                        <span className="sub">No data yet</span>
                      ) : (
                        <>
                          {nf(p.used)} <span className="sub">of {nf(p.plan)}</span> <span className={`chip ${toneOf(p.util, p.low_line, p.watch_line)}`}>{Math.round(p.util)}%</span>
                        </>
                      )}
                    </td>
                    <td>
                      {p.trend == null || p.util == null ? (
                        '-'
                      ) : (
                        <span className={`chip ${p.trend < -15 ? 'cold' : p.trend < -5 ? 'warm' : p.trend >= 5 ? 'hot' : ''}`}>
                          {p.trend > 0 ? '+' : ''}
                          {Math.round(p.trend)}%
                        </span>
                      )}
                    </td>
                    <td>
                      <Spark values={p.days.map((d) => d.util)} est={p.days.map((d) => d.is_estimated)} w={100} h={28} color={p.trend != null && p.trend < -5 ? '#E11D48' : '#5B4BE8'} />
                      {daysInfo(p.days).drawable && daysInfo(p.days).est ? <div className="sub" style={{ fontSize: 11 }}>partly estimated</div> : null}
                    </td>
                    <td className="sub">{ex.length ? ex.map((e) => `${e.label} ${nf(e.value)}${e.unit === '%' ? '%' : ''}`).join(', ') : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="empty">
            <b>No products yet</b>Add the client’s products to see their usage here.
          </div>
        )}
      </div>

      <div className="cols c2" style={{ marginBottom: 14 }}>
        <div>
          <div className="lbl" style={{ marginBottom: 6 }}>
            Engagement this month
          </div>
          <div style={{ display: 'grid', gap: 5 }}>
            {b.engagement.length ? (
              b.engagement.map((e) => (
                <div key={e.activity_key} className="split" style={{ gap: 8 }} title={e.last_note ?? undefined}>
                  <span className={`chip ${STATE_CLS[e.state]}`} style={{ minWidth: 92, justifyContent: 'center' }}>
                    {e.label}
                  </span>
                  <span style={{ fontSize: 13 }}>{e.name}</span>
                  <span className="sub" style={{ marginLeft: 'auto' }}>
                    {e.booked_for ? e.sub_label : agoLabel(e.last_done_on, b.as_of)}
                  </span>
                </div>
              ))
            ) : (
              <div className="sub">No engagement activities are switched on.</div>
            )}
          </div>
        </div>
        <div>
          <div className="lbl" style={{ marginBottom: 6 }}>
            What is open
          </div>
          <dl className="kv">
            <dt>Tasks</dt>
            <dd>
              {o.tasks}
              {o.overdue_tasks ? <span className="chip cold" style={{ marginLeft: 6 }}>{o.overdue_tasks} overdue</span> : null}
            </dd>
            <dt>Escalations</dt>
            <dd>{o.escalations || 'None'}</dd>
            <dt>Client requests</dt>
            <dd>{o.client_requests || 'None'}</dd>
            <dt>Last heard</dt>
            <dd>{o.last_heard_days == null ? 'Not recorded yet' : o.last_heard_days === 0 ? 'Today' : `${o.last_heard_days} days ago`}</dd>
            <dt>Contact</dt>
            <dd>{o.contact ? `${o.contact.name}${o.contact.title ? ` (${o.contact.title})` : ''}` : 'No contact yet'}</dd>
          </dl>
        </div>
      </div>

      <div className="lbl" style={{ marginBottom: 6 }}>
        Ideas to lift adoption
      </div>
      <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
        {b.ideas.length ? (
          b.ideas.map((i, n) => (
            <div key={n} className={`note ${i.kind === 'high' ? 'good' : i.kind === 'fall' ? 'warn' : ''}`} style={{ margin: 0 }}>
              <b>{i.topic}.</b> {i.text}
            </div>
          ))
        ) : (
          <div className="sub">Nothing needs changing. Keep the rhythm going.</div>
        )}
      </div>

      <div className="cols c2">
        <div>
          <div className="lbl" style={{ marginBottom: 6 }}>
            Last touchpoints
          </div>
          {b.last_touchpoints.length ? (
            <div style={{ display: 'grid', gap: 6 }}>
              {b.last_touchpoints.map((t, i) => (
                <div key={i} className="split" style={{ gap: 8, fontSize: 13 }}>
                  <span>{t.label}</span>
                  <span className="sub" style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                    {t.by ? `${firstName(t.by.name)}, ` : ''}
                    {dayLabel(t.at.slice(0, 10))}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="sub">No contact recorded yet.</div>
          )}
        </div>
        <div>
          <div className="lbl" style={{ marginBottom: 6 }}>
            Targets this quarter
          </div>
          {b.targets.length ? (
            <div style={{ display: 'grid', gap: 6 }}>
              {b.targets.map((t) => (
                <div key={t.id} className="split" style={{ gap: 8, fontSize: 13 }}>
                  <span>{t.label}</span>
                  <span className="sub" style={{ marginLeft: 'auto' }}>
                    {nf(t.current_value)} of {nf(t.target_value)}
                  </span>
                  {/* done_pct is progress ("900 of 1,000" → 90% done); pct is the balance share, which would read backwards here. */}
                  <span className={`chip ${TARGET_CLS[t.state]}`} title={TARGET_STATE_LABEL[t.state]}>{t.done_pct}% done</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="sub">No targets set for this quarter.</div>
          )}
        </div>
      </div>
    </Modal>
  );
}
